import * as React from 'react';

import { filterValuesEqual, normalizeFilterValues } from './filter-codec';
import { nonEmpty } from './list-state';
import type { DataTableView, ListQuery, ListState } from './types';
import { usePersistedState } from './use-persisted-state';

/** A view is active when the list shows exactly its query (sort only when the view sets one). */
export function isViewActive(view: DataTableView, list: ListQuery): boolean {
  if (nonEmpty(view.query.q) !== nonEmpty(list.q)) return false;
  if (!filterValuesEqual(view.query.filters ?? {}, list.filters)) return false;
  return view.query.sort === undefined || view.query.sort === list.sort;
}

/** Whether the list shows at least the view's query: filters added on top still count. */
function listIncludesView(view: DataTableView, list: ListQuery): boolean {
  const q = nonEmpty(view.query.q);
  if (q !== undefined && q !== nonEmpty(list.q)) return false;
  if (view.query.sort !== undefined && view.query.sort !== list.sort) return false;
  return Object.entries(view.query.filters ?? {}).every(([key, value]) => {
    const applied = list.filters[key];
    return applied !== undefined && filterValuesEqual({ [key]: value }, { [key]: applied });
  });
}

const viewWeight = (view: DataTableView) =>
  Object.keys(view.query.filters ?? {}).length +
  (nonEmpty(view.query.q) === undefined ? 0 : 1) +
  (view.query.sort === undefined ? 0 : 1);

/**
 * The view the list is showing: the most specific one its query includes, the picked one on a
 * tie. So the menu follows the filters: removing the status chip goes back to "All", typing
 * `status:draft` moves to "Draft", and a filter added on top keeps the name.
 */
function viewShown(
  all: readonly DataTableView[],
  pickedId: string | undefined,
  list: ListQuery,
): DataTableView | undefined {
  const included = all.filter((view) => listIncludesView(view, list));
  const weight = Math.max(-1, ...included.map(viewWeight));
  const best = included.filter((view) => viewWeight(view) === weight);
  return best.find((view) => view.id === pickedId) ?? best[0] ?? all[0];
}

/** Only what a view restores: text, filters, and sort when one is set. */
function snapshot(query: ListQuery): ListQuery {
  const q = nonEmpty(query.q);
  const sort = nonEmpty(query.sort);
  return {
    ...(q ? { q } : {}),
    ...(sort ? { sort } : {}),
    filters: normalizeFilterValues(query.filters),
  };
}

function isViewList(value: unknown): value is DataTableView[] {
  return (
    Array.isArray(value) &&
    value.every(
      (view) =>
        typeof view === 'object' &&
        view !== null &&
        typeof (view as DataTableView).id === 'string' &&
        typeof (view as DataTableView).label === 'string' &&
        typeof (view as DataTableView).query === 'object',
    )
  );
}

function newViewId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `view-${crypto.randomUUID()}`
    : `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const NO_VIEWS: DataTableView[] = [];

export interface UseDataTableViewsOptions {
  list: ListState;
  /** Built-in views; the first is the default ("All"). They cannot be renamed or deleted. */
  views?: readonly DataTableView[];
  /**
   * Saved views, controlled - for storing them on a server so they are shared
   * with the team, as Shopify does. Omit to keep them in this browser under
   * `storageKey`.
   */
  savedViews?: readonly DataTableView[];
  onSavedViewsChange?: (views: DataTableView[]) => void;
  /** `localStorage` key for uncontrolled saved views. Without it (and without `savedViews`) views cannot be saved. */
  storageKey?: string;
}

export function useDataTableViews({
  list,
  views = NO_VIEWS,
  savedViews: savedProp,
  onSavedViewsChange,
  storageKey,
}: UseDataTableViewsOptions) {
  const [stored, setStored] = usePersistedState<DataTableView[]>(
    savedProp ? undefined : storageKey,
    NO_VIEWS,
    isViewList,
  );
  const saved = savedProp ?? stored;
  const canPersist = savedProp !== undefined || storageKey !== undefined;
  const writeSaved = React.useCallback(
    (next: DataTableView[]) => {
      if (!savedProp) setStored(next);
      onSavedViewsChange?.(next);
    },
    [onSavedViewsChange, savedProp, setStored],
  );

  const all = React.useMemo(() => [...views, ...saved], [saved, views]);
  const active = all.find((view) => isViewActive(view, list));

  // Filters added on top of a view keep its name, and "Cancel" returns to its query.
  const [selectedId, setSelectedId] = React.useState(() => active?.id ?? views[0]?.id);
  const selected = viewShown(all, selectedId, list);
  const isSaved = (id: string | undefined) =>
    id !== undefined && saved.some((view) => view.id === id);
  const selectedIsSaved = isSaved(selected?.id);

  const baseQuery: ListQuery = selected ? { filters: {}, ...selected.query } : { filters: {} };
  const isModified =
    nonEmpty(list.q) !== nonEmpty(baseQuery.q) ||
    !filterValuesEqual(list.filters, baseQuery.filters) ||
    (baseQuery.sort !== undefined && baseQuery.sort !== list.sort);

  const current = (): ListQuery => ({
    q: list.q,
    sort: list.sort,
    filters: list.filters,
  });

  const select = (view: DataTableView) => {
    setSelectedId(view.id);
    if (view.pillFilter !== undefined) {
      // Off with the pill's filter and whatever the view being left set, on with the new value.
      const leaving = new Set([view.pillFilter, ...Object.keys(selected?.query.filters ?? {})]);
      const kept = Object.entries(list.filters).filter(([key]) => !leaving.has(key));
      list.update({
        q: selected?.query.q === undefined ? list.q : undefined,
        filters: { ...Object.fromEntries(kept), ...view.query.filters },
      });
      return;
    }
    list.update({
      q: view.query.q,
      filters: view.query.filters ?? {},
      ...(view.query.sort !== undefined ? { sort: view.query.sort } : {}),
    });
  };

  /** Back to the selected view's own query ("Cancel" / the clear button). */
  const revert = () => {
    list.update({
      q: baseQuery.q,
      filters: baseQuery.filters,
      ...(baseQuery.sort !== undefined ? { sort: baseQuery.sort } : {}),
    });
  };

  /** "Save as": a new view from the current query, which becomes selected. */
  const saveAs = (label: string) => {
    const view: DataTableView = {
      id: newViewId(),
      label: label.trim(),
      query: snapshot(current()),
    };
    writeSaved([...saved, view]);
    setSelectedId(view.id);
    return view;
  };

  /** "Save": write the current query into the selected saved view. */
  const saveSelected = () => {
    if (!selected || !selectedIsSaved) return;
    writeSaved(
      saved.map((view) =>
        view.id === selected.id ? { ...view, query: snapshot(current()) } : view,
      ),
    );
  };

  const rename = (id: string, label: string) => {
    writeSaved(saved.map((view) => (view.id === id ? { ...view, label: label.trim() } : view)));
  };

  const duplicate = (id: string, label: string) => {
    const source = all.find((view) => view.id === id);
    if (!source) return undefined;
    const view: DataTableView = {
      id: newViewId(),
      label: label.trim(),
      query: source.query,
    };
    writeSaved([...saved, view]);
    return view;
  };

  const remove = (id: string) => {
    writeSaved(saved.filter((view) => view.id !== id));
    if (id === selected?.id && views[0]) select(views[0]);
  };

  /** Whether `label` is free (view names are unique, case-insensitively). */
  const isLabelAvailable = (label: string, exceptId?: string) => {
    const wanted = label.trim().toLowerCase();
    return (
      wanted !== '' &&
      !all.some((view) => view.id !== exceptId && view.label.toLowerCase() === wanted)
    );
  };

  return {
    enabled: views.length > 0,
    views,
    savedViews: saved,
    selected,
    activeId: active?.id,
    isSaved,
    selectedIsSaved,
    isModified,
    canSave: canPersist && views.length > 0,
    select,
    revert,
    saveAs,
    saveSelected,
    rename,
    duplicate,
    remove,
    isLabelAvailable,
  };
}

export type UseDataTableViewsResult = ReturnType<typeof useDataTableViews>;
